import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import {
  getDashboardRules,
  createDashboardRule,
  updateDashboardRule,
  deleteDashboardRule,
} from "../services/dashboardApi";

const actionClass = (action) => {
  if (action === "ALLOW") {
    return "bg-success-focus text-success-main";
  }

  if (action === "BLOCK") {
    return "bg-danger-focus text-danger-main";
  }

  if (action === "REVIEW") {
    return "bg-warning-focus text-warning-main";
  }

  if (action === "CHALLENGE") {
    return "bg-info-focus text-info-main";
  }

  return "bg-neutral-200 text-neutral-600";
};


const RULE_CATEGORIES = {
  Email: [
    {
      key: "disposable_email",
      label: "Disposable email",
      description: "Signup uses a disposable or temporary email address",
      condition: {"==":[{"var":"email.signals.disposable"},true]},
      defaultAction: "BLOCK",
    },
    {
      key: "young_domain",
      label: "Young email domain",
      description: "Email domain is 60 days old or newer",
      condition: {"<=":[{"var":"email.signals.ageDays"},60]},
      defaultAction: "REVIEW",
    },
    {
      key: "missing_mx",
      label: "Missing mail servers",
      description: "Email domain has no MX mail records",
      condition: {"==":[{"var":"email.signals.hasMX"},false]},
      defaultAction: "REVIEW",
    },
  ],

  Network: [
    {
      key: "tor",
      label: "Tor detected",
      description: "Signup originates from a known Tor exit node",
      condition: {"==":[{"var":"network.signals.isTor"},true]},
      defaultAction: "BLOCK",
    },
    {
      key: "vpn_proxy",
      label: "VPN / Proxy detected",
      description: "Signup originates from a VPN or proxy",
      condition: {"==":[{"var":"network.signals.isVpnProxy"},true]},
      defaultAction: "REVIEW",
    },
    {
      key: "datacenter",
      label: "Datacenter IP detected",
      description: "Signup originates from a cloud or datacenter IP",
      condition: {"==":[{"var":"network.signals.isDatacenter"},true]},
      defaultAction: "REVIEW",
    },
    {
      key: "threat_intel",
      label: "Threat intelligence match",
      description: "IP address appears in threat intelligence feeds",
      condition: {"==":[{"var":"network.signals.isThreatListed"},true]},
      defaultAction: "BLOCK",
    },
  ],

  Device: [
    {
      key: "known_device",
      label: "Previously seen device",
      description: "Device has been seen before",
      condition: {"==":[{"var":"device.known"},true]},
      defaultAction: "REVIEW",
    },
    {
      key: "previous_blocks",
      label: "Device previously blocked",
      description: "Device has one or more previous blocked signups",
      condition: {">=":[{"var":"device.previousBlocks"},1]},
      defaultAction: "BLOCK",
    },
    {
      key: "multiple_emails",
      label: "Device used multiple emails",
      description: "Device has been used with 3 or more email addresses",
      condition: {">=":[{"var":"device.uniqueEmails"},3]},
      defaultAction: "REVIEW",
    },
  ],

  Behaviour: [
    {
      key: "high_bot_score",
      label: "High bot score",
      description: "Signup behaviour has a bot score of 85 or higher",
      condition: {">=":[{"var":"behaviour.botScore"},85]},
      defaultAction: "BLOCK",
    },
    {
      key: "no_mouse",
      label: "No mouse movement",
      description: "No mouse movement was detected during signup",
      condition: {"==":[{"var":"behaviour.mouseMovement"},false]},
      defaultAction: "REVIEW",
    },
    {
      key: "challenge_failed",
      label: "Challenge not solved",
      description: "Signup did not successfully solve the challenge",
      condition: {"==":[{"var":"behaviour.challengeSolved"},false]},
      defaultAction: "BLOCK",
    },
  ],
};

const firstPreset = (category) => RULE_CATEGORIES[category]?.[0];

const RulesLayer = () => {
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [ruleError, setRuleError] = useState("");
  const [ruleSaving, setRuleSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("ALL");

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [toggleRuleConfirm, setToggleRuleConfirm] = useState(null);
  const [deleteRuleConfirm, setDeleteRuleConfirm] = useState(null);
  const [editingRule, setEditingRule] = useState(null);

  const [newRule, setNewRule] = useState({
    category: "Email",
    preset: "disposable_email",
    action: "BLOCK",
  });

  const normalizeRule = (rule) => ({
    ...rule,
    description: rule.description || "",
    condition:
      typeof rule.condition === "string"
        ? rule.condition
        : JSON.stringify(rule.condition),
    action:
      typeof rule.action === "string"
        ? rule.action.toUpperCase()
        : String(rule.action?.type || "FLAG").toUpperCase(),
    triggered:
      Number(
        rule.triggered ??
        rule.triggered_count ??
        rule.matched_count ??
        0
      ),
  });

  const loadRules = async () => {
    try {
      setRulesLoading(true);
      setRuleError("");

      const response = await getDashboardRules({
        includeDisabled: true,
      });

      const liveRules = response?.rules || [];

      setRules(liveRules.map(normalizeRule));
    } catch (error) {
      setRuleError(
        error?.message || "Unable to load rules."
      );
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const parseCondition = (value) => {
    try {
      const parsed = JSON.parse(value);

      if (
        !parsed ||
        typeof parsed !== "object" ||
        Array.isArray(parsed)
      ) {
        throw new Error();
      }

      return parsed;
    } catch {
      throw new Error(
        'Condition must be valid JSON, for example: {"==":[{"var":"network.signals.isTor"},true]}'
      );
    }
  };

  const filteredRules = rules.filter((rule) => {
    const value = search.toLowerCase();

    const matchesSearch =
      String(rule.name || "").toLowerCase().includes(value) ||
      String(rule.description || "").toLowerCase().includes(value) ||
      String(rule.condition || "").toLowerCase().includes(value);

    const matchesAction =
      actionFilter === "ALL" || rule.action === actionFilter;

    return matchesSearch && matchesAction;
  });

  const resetRuleFilters = () => {
    setSearch("");
    setActionFilter("ALL");
  };

  const createRule = async () => {
    if (
      ruleSaving ||
      !newRule.category ||
      !newRule.preset
    ) {
      return;
    }

    try {
      setRuleSaving(true);
      setRuleError("");

      const preset = RULE_CATEGORIES[newRule.category]?.find(
        (item) => item.key === newRule.preset
      );

      if (!preset) {
        throw new Error("Please select a rule type.");
      }

      const condition = preset.condition;

      const actionLabel = {
        BLOCK: "Block",
        REVIEW: "Review",
        ALLOW: "Allow",
      }[newRule.action] || newRule.action;

      const generatedName = `${actionLabel} ${preset.label}`;

      const response = await createDashboardRule({
        name: generatedName,
        description:
          preset.description ||
          "Custom fraud prevention rule.",
        condition,
        action: {
          type: newRule.action.toLowerCase(),
        },
        enabled: true,
        priority: 100,
      });

      const created = response?.rule || response;

      setRules((currentRules) => [
        normalizeRule(created),
        ...currentRules,
      ]);

      setNewRule({
        category: "Email",
        preset: "disposable_email",
        action: "BLOCK",
      });

      setShowCreateModal(false);
    } catch (error) {
      setRuleError(
        error?.message || "Unable to create rule."
      );
    } finally {
      setRuleSaving(false);
    }
  };

  const confirmToggleRule = async () => {
    if (!toggleRuleConfirm || ruleSaving) return;

    try {
      setRuleSaving(true);
      setRuleError("");

      const response = await updateDashboardRule(
        toggleRuleConfirm.id,
        {
          enabled: !toggleRuleConfirm.enabled,
          change_reason: toggleRuleConfirm.enabled
            ? "Disabled from dashboard"
            : "Enabled from dashboard",
        }
      );

      const updated = normalizeRule(
        response?.rule || response
      );

      setRules((currentRules) =>
        currentRules.map((rule) =>
          rule.id === updated.id ? updated : rule
        )
      );

      setToggleRuleConfirm(null);
    } catch (error) {
      setRuleError(
        error?.message || "Unable to update rule."
      );
    } finally {
      setRuleSaving(false);
    }
  };

  const openEditRule = (rule) => {
    setRuleError("");
    setEditingRule({ ...rule });
  };

  const saveEditedRule = async () => {
    if (!editingRule || ruleSaving) {
      return;
    }

    try {
      setRuleSaving(true);
      setRuleError("");

      const response = await updateDashboardRule(
        editingRule.id,
        {
          action: {
            type: editingRule.action.toLowerCase(),
          },
          change_reason: "Action changed from dashboard",
        }
      );

      const updated = normalizeRule(
        response?.rule || response
      );

      setRules((currentRules) =>
        currentRules.map((rule) =>
          rule.id === updated.id ? updated : rule
        )
      );

      setEditingRule(null);
    } catch (error) {
      setRuleError(
        error?.message || "Unable to save rule."
      );
    } finally {
      setRuleSaving(false);
    }
  };

  const confirmDeleteRule = async () => {
    if (!deleteRuleConfirm || ruleSaving) return;

    try {
      setRuleSaving(true);
      setRuleError("");

      await deleteDashboardRule(
        deleteRuleConfirm.id
      );

      setRules((currentRules) =>
        currentRules.filter(
          (rule) =>
            rule.id !== deleteRuleConfirm.id
        )
      );

      setDeleteRuleConfirm(null);
    } catch (error) {
      setRuleError(
        error?.message || "Unable to delete rule."
      );
    } finally {
      setRuleSaving(false);
    }
  };

  return (
    <>
      {/* =====================================================
          RULES CARD
      ===================================================== */}
      <div className="card">
        <div className="card-header border-bottom bg-base py-20 px-24">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 className="mb-1">
                Fraud Rules
              </h5>

              <p className="text-secondary-light text-sm mb-0">
                Control how Simplif handles suspicious signup activity
              </p>
            </div>

            <div className="d-flex align-items-center gap-2">
              {/* REFRESH RULES */}
              <button
                type="button"
                onClick={loadRules}
                title="Refresh rules"
                aria-label="Refresh rules"
                className="simplif-reset-icon"
                disabled={rulesLoading}
              >
                <Icon
                  icon="solar:restart-outline"
                  className={`text-xl ${
                    rulesLoading ? "spinner-border spinner-border-sm" : ""
                  }`}
                />
              </button>

              {/* CREATE RULE */}
              <button
                type="button"
                className="btn btn-primary-600 d-flex align-items-center gap-2"
                onClick={() => {
                  setRuleError("");
                  setShowCreateModal(true);
                }}
              >
                <Icon icon="solar:add-circle-outline" />
                Create Rule
              </button>
            </div>
          </div>
        </div>

        <div className="card-body p-24">
          {/* =====================================================
              FILTERS
          ===================================================== */}
          <div className="row gy-3 mb-24">
            {/* SEARCH */}
            <div className="col-lg-6">
              <label className="form-label fw-semibold">
                Search
              </label>

              <div className="position-relative">
                <input
                  type="text"
                  className="form-control radius-8"
                  placeholder="Search rules..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <Icon
                  icon="solar:magnifer-outline"
                  className="position-absolute top-50 end-0 translate-middle-y me-16 text-secondary-light"
                />
              </div>
            </div>

            {/* ACTION */}
            <div className="col-lg-6">
              <label className="form-label fw-semibold">
                Action
              </label>

              <select
                className="form-select radius-8"
                value={actionFilter}
                onChange={(e) =>
                  setActionFilter(e.target.value)
                }
              >
                <option value="ALL">
                  All actions
                </option>

                <option value="BLOCK">
                  Block
                </option>

                <option value="REVIEW">
                  Review
                </option>

                <option value="ALLOW">
                  Allow
                </option>
              </select>
            </div>
          </div>

          {/* =====================================================
              RULES TABLE
          ===================================================== */}
          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Condition</th>
                  <th>Action</th>
                  <th>Triggered</th>
                  <th>Status</th>
                  <th className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredRules.length > 0 ? (
                  filteredRules.map((rule) => (
                    <tr key={rule.id}>
                      {/* RULE */}
                      <td>
                        <div>
                          <span className="fw-semibold d-block mb-4">
                            {rule.name}
                          </span>

                          <span className="text-secondary-light text-sm">
                            {rule.description}
                          </span>
                        </div>
                      </td>

                      {/* CONDITION */}
                      <td>
                        <span className="bg-neutral-100 px-12 py-6 rounded-pill text-sm fw-medium">
                          {rule.description || rule.name}
                        </span>
                      </td>

                      {/* ACTION */}
                      <td>
                        <span
                          className={`px-12 py-6 rounded-pill text-sm fw-semibold ${actionClass(
                            rule.action
                          )}`}
                        >
                          {rule.action}
                        </span>
                      </td>

                      {/* TRIGGERED */}
                      <td>
                        <span className="fw-semibold">
                          {rule.triggered}
                        </span>
                      </td>

                      {/* STATUS */}
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setToggleRuleConfirm(rule)
                            }
                            className={`border-0 rounded-pill position-relative ${
                              rule.enabled
                                ? "bg-success-main"
                                : "bg-neutral-300"
                            }`}
                            style={{
                              width: "44px",
                              height: "24px",
                              cursor: "pointer",
                              padding: 0,
                              transition: "0.2s",
                            }}
                            aria-label={
                              rule.enabled
                                ? "Disable rule"
                                : "Enable rule"
                            }
                          >
                            <span
                              className="position-absolute bg-white rounded-circle"
                              style={{
                                width: "18px",
                                height: "18px",
                                top: "3px",
                                left: rule.enabled
                                  ? "23px"
                                  : "3px",
                                transition: "0.2s",
                              }}
                            />
                          </button>

                          <span
                            className={
                              rule.enabled
                                ? "text-success-main fw-medium"
                                : "text-secondary-light"
                            }
                          >
                            {rule.enabled
                              ? "Active"
                              : "Disabled"}
                          </span>
                        </div>
                      </td>

                      {/* ACTION BUTTONS */}
                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            type="button"
                            className="w-36-px h-36-px bg-primary-50 text-primary-600 rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                            title="Edit rule"
                            onClick={() =>
                              openEditRule(rule)
                            }
                          >
                            <Icon icon="solar:pen-outline" />
                          </button>

                          <button
                            type="button"
                            className="w-36-px h-36-px bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                            title="Delete rule"
                            onClick={() =>
                              setDeleteRuleConfirm(rule)
                            }
                          >
                            <Icon icon="solar:trash-bin-trash-outline" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center py-40"
                    >
                      <Icon
                        icon="solar:bolt-outline"
                        className="text-3xl text-secondary-light mb-8"
                      />

                      <p className="mb-0 text-secondary-light">
                        No rules found.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* =====================================================
          CREATE RULE MODAL
      ===================================================== */}
      {showCreateModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowCreateModal(false)}
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content radius-12 border-0 shadow-lg">
                <div className="modal-header px-24 py-20">
                  <div>
                    <h5 className="modal-title mb-1">
                      Create Rule
                    </h5>

                    <p className="text-secondary-light text-sm mb-0">
                      Create a custom signup fraud rule
                    </p>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() =>
                      setShowCreateModal(false)
                    }
                  />
                </div>

                <div className="modal-body p-24">
                  {/* CATEGORY */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold">
                      Category
                    </label>

                    <select
                      className="form-select"
                      value={newRule.category}
                      onChange={(e) => {
                        const category = e.target.value;
                        const preset = firstPreset(category);

                        setNewRule({
                          ...newRule,
                          category,
                          preset: preset?.key || "",
                          action: preset?.defaultAction || "REVIEW",
                        });
                      }}
                    >
                      {Object.keys(RULE_CATEGORIES).map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* RULE TYPE */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold">
                      Rule type
                    </label>

                    <select
                      className="form-select"
                      value={newRule.preset}
                      onChange={(e) => {
                        const preset = RULE_CATEGORIES[
                          newRule.category
                        ].find((item) => item.key === e.target.value);

                        setNewRule({
                          ...newRule,
                          preset: e.target.value,
                          action: preset?.defaultAction || newRule.action,
                        });
                      }}
                    >
                      {RULE_CATEGORIES[newRule.category].map((preset) => (
                        <option key={preset.key} value={preset.key}>
                          {preset.label}
                        </option>
                      ))}
                    </select>

                    <div className="text-secondary-light text-sm mt-8">
                      {
                        RULE_CATEGORIES[newRule.category].find(
                          (item) => item.key === newRule.preset
                        )?.description
                      }
                    </div>
                  </div>

                  {/* ACTION */}
                  <div>
                    <label className="form-label fw-semibold">
                      Action
                    </label>

                    <select
                      className="form-select"
                      value={newRule.action}
                      onChange={(e) =>
                        setNewRule({
                          ...newRule,
                          action: e.target.value,
                        })
                      }
                    >
                      <option value="BLOCK">
                        Block
                      </option>

                      <option value="REVIEW">
                        Review
                      </option>

                      <option value="ALLOW">
                        Allow
                      </option>
                    </select>

                    <div className="text-secondary-light text-sm mt-8">
                      This will create:
                      {" "}
                      <strong>
                        {{
                          BLOCK: "Block",
                          REVIEW: "Review",
                          ALLOW: "Allow",
                        }[newRule.action] || newRule.action}
                        {" "}
                        {
                          RULE_CATEGORIES[newRule.category]?.find(
                            (item) => item.key === newRule.preset
                          )?.label
                        }
                      </strong>
                    </div>
                  </div>
                </div>

                {ruleError && (
                  <div className="px-24 pb-4">
                    <div
                      className="alert alert-danger mb-0 py-12 px-16"
                      role="alert"
                    >
                      {ruleError}
                    </div>
                  </div>
                )}

                <div className="modal-footer px-24 py-20">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() =>
                      setShowCreateModal(false)
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary-600"
                    onClick={createRule}
                    disabled={ruleSaving}
                  >
                    <Icon
                      icon="solar:add-circle-outline"
                      className="me-2"
                    />
                    {ruleSaving ? "Creating..." : "Create Rule"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* =====================================================
          EDIT RULE MODAL
      ===================================================== */}
      {editingRule && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setEditingRule(null)}
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content radius-12 border-0 shadow-lg">
                <div className="modal-header px-24 py-20">
                  <div>
                    <h5 className="modal-title mb-1">
                      Edit Rule
                    </h5>

                    <p className="text-secondary-light text-sm mb-0">
                      Update this fraud rule
                    </p>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() =>
                      setEditingRule(null)
                    }
                  />
                </div>

                <div className="modal-body p-24">
                  <div className="mb-20">
                    <label className="form-label fw-semibold">
                      Rule
                    </label>

                    <div className="border rounded-3 p-16 bg-neutral-50">
                      <div className="fw-semibold">
                        {editingRule.name}
                      </div>

                      {editingRule.description && (
                        <div className="text-secondary-light text-sm mt-4">
                          {editingRule.description}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="form-label fw-semibold">
                      Action
                    </label>

                    <select
                      className="form-select"
                      value={editingRule.action}
                      onChange={(e) =>
                        setEditingRule({
                          ...editingRule,
                          action: e.target.value,
                        })
                      }
                    >
                      <option value="BLOCK">
                        Block
                      </option>

                      <option value="REVIEW">
                        Review
                      </option>

                      <option value="ALLOW">
                        Allow
                      </option>
                    </select>

                    <div className="text-secondary-light text-sm mt-8">
                      Change what Simplif does when this rule matches.
                    </div>
                  </div>
                </div>

                <div className="modal-footer px-24 py-20">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() =>
                      setEditingRule(null)
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary-600"
                    onClick={saveEditedRule}
                  >
                    <Icon
                      icon="solar:diskette-outline"
                      className="me-2"
                    />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* =====================================================
          ACTIVATE / DEACTIVATE CONFIRMATION
      ===================================================== */}
      {toggleRuleConfirm && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() =>
              setToggleRuleConfirm(null)
            }
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content radius-12 border-0 shadow-lg">
                <div className="modal-body p-32 text-center">
                  <div
                    className={`w-64-px h-64-px rounded-circle d-inline-flex align-items-center justify-content-center mb-20 ${
                      toggleRuleConfirm.enabled
                        ? "bg-warning-focus text-warning-main"
                        : "bg-success-focus text-success-main"
                    }`}
                  >
                    <Icon
                      icon={
                        toggleRuleConfirm.enabled
                          ? "solar:pause-circle-outline"
                          : "solar:play-circle-outline"
                      }
                      className="text-3xl"
                    />
                  </div>

                  <h5 className="mb-12">
                    {toggleRuleConfirm.enabled
                      ? "Deactivate rule?"
                      : "Activate rule?"}
                  </h5>

                  <p className="text-secondary-light mb-8">
                    {toggleRuleConfirm.enabled
                      ? "Are you sure you would like to deactivate this rule? It will stop affecting new signup evaluations."
                      : "Are you sure you would like to activate this rule? It will begin affecting new signup evaluations."}
                  </p>

                  <p className="fw-semibold mb-28">
                    {toggleRuleConfirm.name}
                  </p>

                  {ruleError && (
                    <div
                      className="alert alert-danger text-start mb-20"
                      role="alert"
                    >
                      <div className="fw-semibold mb-1">
                        {toggleRuleConfirm.enabled
                          ? "Cannot deactivate this rule"
                          : "Unable to activate this rule"}
                      </div>
                      <div>{ruleError}</div>
                    </div>
                  )}


                  <div className="d-flex justify-content-center gap-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-24"
                      onClick={() =>
                        setToggleRuleConfirm(null)
                      }
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className={
                        toggleRuleConfirm.enabled
                          ? "btn btn-warning-600 px-24"
                          : "btn btn-success-600 px-24"
                      }
                      onClick={confirmToggleRule}
                    >
                      {toggleRuleConfirm.enabled
                        ? "Deactivate Rule"
                        : "Activate Rule"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* =====================================================
          DELETE CONFIRMATION
      ===================================================== */}
      {deleteRuleConfirm && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() =>
              setDeleteRuleConfirm(null)
            }
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content radius-12 border-0 shadow-lg">
                <div className="modal-body p-32 text-center">
                  <div className="w-64-px h-64-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center mb-20">
                    <Icon
                      icon="solar:trash-bin-trash-outline"
                      className="text-3xl"
                    />
                  </div>

                  <h5 className="mb-12">
                    Delete rule?
                  </h5>

                  <p className="text-secondary-light mb-8">
                    Are you sure you would like to delete this rule?
                  </p>

                  <p className="text-secondary-light mb-8">
                    This action cannot be undone.
                  </p>

                  <p className="fw-semibold mb-28">
                    {deleteRuleConfirm.name}
                  </p>

                  {ruleError && (
                    <div
                      className="alert alert-danger text-start mb-20"
                      role="alert"
                    >
                      <div className="fw-semibold mb-1">
                        Cannot delete this rule
                      </div>
                      <div>{ruleError}</div>
                    </div>
                  )}


                  <div className="d-flex justify-content-center gap-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-24"
                      onClick={() =>
                        setDeleteRuleConfirm(null)
                      }
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="btn btn-danger-600 px-24"
                      onClick={confirmDeleteRule}
                    >
                      <Icon
                        icon="solar:trash-bin-trash-outline"
                        className="me-2"
                      />
                      Delete Rule
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* =====================================================
          RESET ICON STYLE
      ===================================================== */}
      <style>
        {`
          .simplif-reset-icon {
            width: 38px;
            height: 38px;
            padding: 0;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            background: #ffffff;
            color: #6b7280;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition:
              color 0.15s ease,
              border-color 0.15s ease,
              background-color 0.15s ease;
          }

          .simplif-reset-icon:hover {
            color: #ff5a00;
            border-color: #ff5a00;
            background: #fff7f2;
          }

          .simplif-reset-icon:focus {
            outline: none;
            box-shadow: 0 0 0 3px rgba(255, 90, 0, 0.12);
          }
        `}
      </style>
    </>
  );
};

export default RulesLayer;

